import type { AdminPermissionApiRule } from '@/types'

export interface AdminPermissionPageDefinition {
  key: string
  label: string
  aliases?: string[]
  section: string
  routePatterns: string[]
  apiRules: AdminPermissionApiRule[]
}

const api = (path: string, method = '*'): AdminPermissionApiRule => ({ method, path })

const pushStatsRules = [api('/admin-api/v1/admin/push/stats/**')]
const pushTemplateRules = [api('/admin-api/v1/admin/push-templates/**')]
const pushTaskRules = [api('/admin-api/v1/admin/push-tasks/**')]
const pushTestRules = [api('/admin-api/v1/admin/push-test/**')]
const pushConfigRules = [api('/admin-api/v1/admin/push-configs/**')]
const pushRecallRules = [
  api('/admin-api/v1/admin/push/recall-config'),
  api('/admin-api/v1/admin/push/recall-logs'),
]

export const ADMIN_PERMISSION_CATALOG: AdminPermissionPageDefinition[] = [
  {
    key: '/analytics/dashboard',
    label: '数据大盘',
    section: '数据分析',
    routePatterns: ['/analytics/dashboard', '/analytics/reports/create', '/analytics/reports/view'],
    apiRules: [api('/admin-api/v1/admin/analytics/**')],
  },
  {
    key: '/campaigns/overview', label: '投放概览', section: '投放管理',
    routePatterns: ['/campaigns/overview'],
    apiRules: [api('/admin-api/v1/admin/campaigns/sites', 'GET'), api('/admin-api/v1/admin/campaigns/stats', 'GET')],
  },
  {
    key: '/campaigns/records', label: '访问与归因', section: '投放管理',
    routePatterns: ['/campaigns/records'],
    apiRules: [api('/admin-api/v1/admin/campaigns/sites', 'GET'), api('/admin-api/v1/admin/campaigns/visits', 'GET'), api('/admin-api/v1/admin/campaigns/export', 'GET')],
  },
  {
    key: '/campaigns/sites', label: '推广站点', section: '投放管理',
    routePatterns: ['/campaigns/sites'],
    apiRules: [api('/admin-api/v1/admin/campaigns/sites'), api('/admin-api/v1/admin/campaigns/sites/:site_id', 'PUT'), api('/admin-api/v1/admin/campaigns/template', 'GET'), api('/admin-api/v1/admin/campaigns/preview/:site_id/:variant', 'GET')],
  },
  {
    key: '/campaigns/rules', label: '规则与判定', section: '投放管理',
    routePatterns: ['/campaigns/rules'],
    apiRules: [api('/admin-api/v1/admin/campaigns/rules', 'GET'), api('/admin-api/v1/admin/campaigns/rules/list', 'GET'), api('/admin-api/v1/admin/campaigns/rules/test', 'POST'), api('/admin-api/v1/admin/campaigns/rules/refresh', 'POST'), api('/admin-api/v1/admin/campaigns/judge', 'POST')],
  },
  {
    key: '/campaigns/integrations', label: '接入状态', section: '投放管理',
    routePatterns: ['/campaigns/integrations'],
    apiRules: [api('/admin-api/v1/admin/campaigns/integrations', 'GET')],
  },
  {
    key: '/service-monitor',
    label: '服务监测',
    section: '服务监测',
    routePatterns: ['/service-monitor'],
    apiRules: [api('/admin-api/v1/admin/service-monitor/**')],
  },
  {
    key: '/observability/user-behavior-monitor',
    label: '行为时间线监控',
    section: '监控分析',
    routePatterns: ['/observability/user-behavior-monitor'],
    apiRules: [api('/admin-api/v1/admin/user-behavior-monitor/**', 'GET')],
  },
  {
    key: '/observability/api-request-stats',
    label: '接口请求统计',
    section: '监控分析',
    routePatterns: ['/observability/api-request-stats'],
    apiRules: [
      api('/admin-api/v1/admin/api-request-stats/overview', 'GET'),
      api('/admin-api/v1/admin/api-request-stats/buckets', 'GET'),
      api('/admin-api/v1/admin/api-request-stats/details', 'GET'),
      api('/admin-api/v1/admin/api-request-stats/recollect', 'POST'),
    ],
  },
  {
    key: '/users',
    label: '用户列表',
    section: '用户管理',
    routePatterns: ['/users'],
    apiRules: [api('/admin-api/v1/admin/users/**')],
  },
  {
    key: '/users/first-24h-swipe-stats',
    label: '24H划卡统计',
    section: '用户管理',
    routePatterns: ['/users/first-24h-swipe-stats'],
    apiRules: [api('/admin-api/v1/admin/users/first-24h-swipe-stats', 'GET')],
  },
  {
    key: '/users/device-attribution-events',
    label: '归因事件',
    section: '用户管理',
    routePatterns: ['/users/device-attribution-events'],
    apiRules: [
      api('/admin-api/v1/admin/device-attribution-events', 'GET'),
      api('/admin-api/v1/admin/users/:id', 'GET'),
      api('/admin-api/v1/admin/users/:id/management-detail', 'GET'),
      api('/admin-api/v1/admin/users/:id/b-side-enabled', 'PUT'),
      api('/admin-api/v1/admin/users/:id/sessions', 'GET'),
      api('/admin-api/v1/admin/users/:id/stats', 'GET'),
      api('/admin-api/v1/admin/users/:id/operation-logs', 'GET'),
    ],
  },
  {
    key: '/users/admins',
    label: '管理员',
    section: '用户管理',
    routePatterns: ['/users/admins'],
    apiRules: [api('/admin-api/v1/admin/accounts/**')],
  },
  {
    key: '/characters',
    label: '角色列表',
    section: '角色管理',
    routePatterns: ['/characters'],
    apiRules: [
      api('/admin-api/v1/admin/characters'),
      api('/admin-api/v1/admin/characters/:id'),
      api('/admin-api/v1/admin/characters/:id/**'),
      api('/admin-api/v1/admin/character-categories/**'),
      api('/admin-api/v1/admin/character-looking-for-tags/**'),
      api('/admin-api/v1/admin/character-tags/**'),
      api('/admin-api/v1/admin/character-type-prefer-tags/**'),
      api('/admin-api/v1/admin/package-service-accounts/**'),
      api('/admin-api/v1/admin/files/upload', 'POST'),
    ],
  },
  {
    key: '/characters/audit',
    label: '角色审核',
    section: '角色管理',
    routePatterns: ['/characters/audit'],
    apiRules: [
      api('/admin-api/v1/admin/characters/pending', 'GET'),
      api('/admin-api/v1/admin/characters/audited', 'GET'),
      api('/admin-api/v1/admin/characters/:id'),
      api('/admin-api/v1/admin/characters/:id/audit-logs', 'GET'),
      api('/admin-api/v1/admin/characters/:id/versions/**'),
    ],
  },
  {
    key: '/characters/categories',
    label: '角色分类',
    section: '角色管理',
    routePatterns: ['/characters/categories'],
    apiRules: [api('/admin-api/v1/admin/character-categories/**')],
  },
  {
    key: '/characters/tags',
    label: '角色标签',
    section: '角色管理',
    routePatterns: ['/characters/tags'],
    apiRules: [api('/admin-api/v1/admin/character-tags/**')],
  },
  {
    key: '/characters/looking-for-tags',
    label: '约会目的标签',
    section: '角色管理',
    routePatterns: ['/characters/looking-for-tags'],
    apiRules: [api('/admin-api/v1/admin/character-looking-for-tags/**')],
  },
  {
    key: '/characters/type-prefer-tags',
    label: '意向类型标签',
    section: '角色管理',
    routePatterns: ['/characters/type-prefer-tags'],
    apiRules: [api('/admin-api/v1/admin/character-type-prefer-tags/**')],
  },
  {
    key: '/characters/template-variables',
    label: '角色模板变量',
    section: '角色管理',
    routePatterns: ['/characters/template-variables'],
    apiRules: [api('/admin-api/v1/admin/character-template-variables/**')],
  },
  {
    key: '/characters/system-template-variables',
    label: '系统模板变量',
    section: '角色管理',
    routePatterns: ['/characters/system-template-variables'],
    apiRules: [api('/admin-api/v1/admin/system-template-variables/**')],
  },
  {
    key: '/characters/stats',
    label: '角色统计',
    section: '角色管理',
    routePatterns: ['/characters/stats'],
    apiRules: [api('/admin-api/v1/admin/character-stats', 'GET')],
  },
  {
    key: '/payment/products',
    label: '商品配置',
    section: '支付管理',
    routePatterns: ['/payment/products'],
    apiRules: [api('/admin-api/v1/admin/products/**')],
  },
  {
    key: '/payment/orders',
    label: '支付订单',
    section: '支付管理',
    routePatterns: ['/payment/orders'],
    apiRules: [api('/admin-api/v1/admin/payment-orders/**')],
  },
  {
    key: '/payment/recharge-users',
    label: '充值用户分析',
    section: '支付管理',
    routePatterns: ['/payment/recharge-users'],
    apiRules: [api('/admin-api/v1/admin/recharge-users/**')],
  },
  {
    key: '/payment/subscriptions',
    label: '订阅管理',
    section: '支付管理',
    routePatterns: ['/payment/subscriptions'],
    apiRules: [api('/admin-api/v1/admin/subscriptions/**')],
  },
  {
    key: '/payment/refunds',
    label: '退款管理',
    section: '支付管理',
    routePatterns: ['/payment/refunds'],
    apiRules: [api('/admin-api/v1/admin/refunds/**')],
  },
  {
    key: '/payment/fulfillments',
    label: '发货监控',
    section: '支付管理',
    routePatterns: ['/payment/fulfillments'],
    apiRules: [api('/admin-api/v1/admin/fulfillments/**')],
  },
  {
    key: '/payment/webhook-logs',
    label: 'Webhook日志',
    section: '支付管理',
    routePatterns: ['/payment/webhook-logs'],
    apiRules: [api('/admin-api/v1/admin/webhook-logs/**')],
  },
  {
    key: '/payment/apple-pay-configs',
    label: 'Apple Pay配置',
    section: '支付管理',
    routePatterns: ['/payment/apple-pay-configs'],
    apiRules: [api('/admin-api/v1/admin/apple-pay-configs/**')],
  },
  {
    key: '/payment/google-pay-configs',
    label: 'Google Pay配置',
    section: '支付管理',
    routePatterns: ['/payment/google-pay-configs'],
    apiRules: [api('/admin-api/v1/admin/google-pay-configs/**')],
  },
  {
    key: '/payment/third-party-configs',
    label: '第三方支付配置',
    section: '支付管理',
    routePatterns: ['/payment/third-party-configs'],
    apiRules: [api('/admin-api/v1/admin/third-party-payment-configs/**')],
  },
  {
    key: '/payment/route-configs',
    label: '支付列表设置',
    section: '支付管理',
    routePatterns: ['/payment/route-configs'],
    apiRules: [api('/admin-api/v1/admin/payment-route-configs/**')],
  },
  {
    key: '/payment/traces',
    label: '三方支付看板',
    section: '支付管理',
    routePatterns: ['/payment/traces'],
    apiRules: [
      api('/admin-api/v1/admin/payment-traces/**', 'GET'),
      api('/admin-api/v1/admin/payment-orders/:id/trace', 'GET'),
    ],
  },
  {
    key: '/operations/rewards',
    label: '奖励管理',
    section: '运营管理',
    routePatterns: ['/operations/rewards'],
    apiRules: [api('/admin-api/v1/admin/rewards/**')],
  },
  {
    key: '/operations/activities',
    label: '活动管理',
    section: '运营管理',
    routePatterns: ['/operations/activities'],
    apiRules: [
      api('/admin-api/v1/admin/activities/**'),
      api('/admin-api/v1/admin/products', 'GET'),
    ],
  },
  {
    key: '/operations/announcements',
    label: '系统公告',
    section: '运营管理',
    routePatterns: ['/operations/announcements'],
    apiRules: [api('/admin-api/v1/admin/announcements/**')],
  },
  {
    key: '/operations/notifications',
    label: '用户通知',
    section: '运营管理',
    routePatterns: ['/operations/notifications'],
    apiRules: [api('/admin-api/v1/admin/notifications/**')],
  },
  {
    key: '/operations/tasks',
    label: '任务系统',
    section: '运营管理',
    routePatterns: ['/operations/tasks'],
    apiRules: [api('/admin-api/v1/admin/tasks/**')],
  },
  {
    key: '/operations/checkin',
    label: '签到配置',
    section: '运营管理',
    routePatterns: ['/operations/checkin'],
    apiRules: [api('/admin-api/v1/admin/checkin-configs/**')],
  },
  {
    key: '/operations/invitation',
    label: '邀请活动',
    section: '运营管理',
    routePatterns: ['/operations/invitation'],
    apiRules: [api('/admin-api/v1/admin/invitation-configs/**')],
  },
  {
    key: '/operations/vip',
    label: 'VIP配置',
    section: '运营管理',
    routePatterns: ['/operations/vip'],
    apiRules: [api('/admin-api/v1/admin/vip-configs/**')],
  },
  {
    key: '/operations/trending-keywords',
    label: '热词管理',
    section: '运营管理',
    routePatterns: ['/operations/trending-keywords'],
    apiRules: [api('/admin-api/v1/admin/trending-keywords/**')],
  },
  {
    key: '/rooms/chat-assets',
    label: '聊天素材',
    section: '聊天管理',
    routePatterns: ['/rooms/chat-assets'],
    apiRules: [
      api('/admin-api/v1/admin/chat-assets/**'),
      api('/admin-api/v1/admin/files/upload', 'POST'),
    ],
  },
  {
    key: '/rooms/chat-corpus',
    label: '短聊语料库',
    section: '聊天管理',
    routePatterns: ['/rooms/chat-corpus'],
    apiRules: [api('/admin-api/v1/admin/chat-corpus/**')],
  },
  {
    key: '/rooms/chat-qa',
    label: '聊天问答库',
    section: '聊天管理',
    routePatterns: ['/rooms/chat-qa'],
    apiRules: [api('/admin-api/v1/admin/chat-qa/**')],
  },
  {
    key: '/rooms/unpaid-follow-up-stats',
    label: '追问效果统计',
    section: '聊天管理',
    routePatterns: ['/rooms/unpaid-follow-up-stats'],
    apiRules: [api('/admin-api/v1/admin/chat/unpaid-follow-up-stats/**', 'GET')],
  },
  {
    key: '/operations/character-generation',
    label: '角色生成配置',
    section: '运营管理',
    routePatterns: ['/operations/character-generation'],
    apiRules: [api('/admin-api/v1/admin/character-generation-configs/**')],
  },
  {
    key: '/push/stats',
    label: '推送统计',
    section: '推送管理',
    routePatterns: ['/push/stats'],
    apiRules: pushStatsRules,
  },
  {
    key: '/push/templates',
    label: '推送模板',
    section: '推送管理',
    routePatterns: ['/push/templates'],
    apiRules: pushTemplateRules,
  },
  {
    key: '/push/tasks',
    label: '推送任务',
    section: '推送管理',
    routePatterns: ['/push/tasks'],
    apiRules: pushTaskRules,
  },
  {
    key: '/push/test',
    label: '推送测试',
    section: '推送管理',
    routePatterns: ['/push/test'],
    apiRules: pushTestRules,
  },
  {
    key: '/push/config',
    label: '推送配置',
    section: '推送管理',
    routePatterns: ['/push/config'],
    apiRules: pushConfigRules,
  },
  {
    key: '/push/recall-logs',
    label: '召回记录',
    section: '推送管理',
    routePatterns: ['/push/recall-logs'],
    apiRules: pushRecallRules,
  },
  {
    key: '/reports',
    label: '举报处理',
    section: '审核管理',
    routePatterns: ['/reports'],
    apiRules: [api('/admin-api/v1/admin/reports/**')],
  },
  {
    key: '/moderation/access-control',
    label: 'IP访问控制',
    section: '审核管理',
    routePatterns: ['/moderation/access-control'],
    apiRules: [api('/admin-api/v1/admin/access-control/**')],
  },
  {
    key: '/rooms',
    label: '房间列表',
    section: '聊天管理',
    routePatterns: ['/rooms'],
    apiRules: [api('/admin-api/v1/admin/rooms/**')],
  },
  {
    key: '/rooms/messages',
    label: '聊天记录',
    section: '聊天管理',
    routePatterns: ['/rooms/messages'],
    apiRules: [api('/admin-api/v1/admin/rooms/**')],
  },
  {
    key: '/rooms/chat-replay',
    label: '聊天回放',
    section: '聊天管理',
    routePatterns: ['/rooms/chat-replay'],
    apiRules: [
      api('/admin-api/v1/admin/chat-replays', 'GET'),
      api('/admin-api/v1/admin/chat-replays', 'POST'),
      api('/admin-api/v1/admin/chat-replays/:id', 'GET'),
      api('/admin-api/v1/admin/chat-replays/:id/run', 'POST'),
      api('/admin-api/v1/admin/rooms/**'),
    ],
  },
  {
    key: '/visual-generation/tasks',
    label: '任务监控',
    section: '视觉生成',
    routePatterns: ['/visual-generation/tasks'],
    apiRules: [
      api('/admin-api/v1/admin/visual-generation/tasks/**'),
      api('/admin-api/v1/admin/visual-generation/summary', 'GET'),
    ],
  },
  {
    key: '/visual-generation/preview',
    label: '项目预览',
    section: '视觉生成',
    routePatterns: ['/visual-generation/preview'],
    apiRules: [
      api('/admin-api/v1/admin/users/:id', 'GET'),
      api('/admin-api/v1/admin/users/:id/management-detail', 'GET'),
      api('/admin-api/v1/admin/users/:id/sessions', 'GET'),
      api('/admin-api/v1/admin/users/:id/stats', 'GET'),
      api('/admin-api/v1/admin/users/:id/assets/change', 'POST'),
      api('/admin-api/v1/admin/users/:id/operation-logs', 'GET'),
    ],
  },
  {
    key: '/visual-replacement/pricing',
    label: '视觉生成全局定价与自定义原价',
    section: '视觉生成',
    routePatterns: ['/visual-replacement/pricing'],
    apiRules: [
      api('/admin-api/v1/admin/visual-replacement/pricing', 'GET'),
      api('/admin-api/v1/admin/visual-replacement/pricing', 'PUT'),
      api('/admin-api/v1/admin/visual-replacement/pricing/impact', 'POST'),
      api('/admin-api/v1/admin/visual-replacement/templates/:id/prices', 'GET'),
      api('/admin-api/v1/admin/visual-replacement/templates/:id/prices', 'PUT'),
      api('/admin-api/v1/admin/visual-replacement/templates/:id/prices/impact', 'POST'),
    ],
  },
  {
    key: '/visual-replacement/promotions',
    label: '视觉生成促销管理',
    section: '视觉生成',
    routePatterns: ['/visual-replacement/promotions'],
    apiRules: [
      api('/admin-api/v1/admin/visual-replacement/promotions', 'GET'),
      api('/admin-api/v1/admin/visual-replacement/promotions', 'POST'),
      api('/admin-api/v1/admin/visual-replacement/promotions/:id', 'GET'),
      api('/admin-api/v1/admin/visual-replacement/promotions/:id', 'PUT'),
      api('/admin-api/v1/admin/visual-replacement/promotions/:id/preview', 'POST'),
      api('/admin-api/v1/admin/visual-replacement/promotions/:id/publish', 'POST'),
      api('/admin-api/v1/admin/visual-replacement/promotions/:id/stop', 'POST'),
      api('/admin-api/v1/admin/visual-replacement/promotions/:id/copy', 'POST'),
      api('/admin-api/v1/admin/visual-replacement/templates', 'GET'),
      api('/admin-api/v1/admin/visual-replacement/template-categories', 'GET'),
    ],
  },
  {
    key: '/visual-replacement/app-configs',
    label: '视觉生成应用配置',
    section: '视觉生成',
    routePatterns: ['/visual-replacement/app-configs'],
    apiRules: [
      api('/admin-api/v1/admin/visual-replacement/app-configs', 'GET'),
      api('/admin-api/v1/admin/visual-replacement/app-configs', 'PUT'),
      api('/admin-api/v1/admin/visual-replacement/app-configs/b-side-enabled', 'PUT'),
      api('/admin-api/v1/admin/visual-replacement/app-configs/r2-public-domain', 'PUT'),
      api('/admin-api/v1/admin/visual-replacement/app-configs/face-preview-images', 'GET'),
      api('/admin-api/v1/admin/visual-replacement/app-configs/face-preview-images', 'PUT'),
      api('/admin-api/v1/admin/visual-replacement/app-configs/face-preview-images/upload', 'POST'),
      api('/admin-api/v1/admin/visual-replacement/models/**', 'GET'),
      api('/admin-api/v1/admin/visual-replacement/prompts/**', 'GET'),
    ],
  },
  {
    key: '/visual-replacement/image/templates',
    label: '图片生成模板管理',
    section: '视觉生成',
    routePatterns: ['/visual-replacement/image/templates'],
    apiRules: [
      api('/admin-api/v1/admin/visual-replacement/templates/**'),
      api('/admin-api/v1/admin/visual-replacement/template-categories/**'),
      api('/admin-api/v1/admin/visual-generation/upload', 'POST'),
      api('/admin-api/v1/admin/visual-generation/upload-batch', 'POST'),
    ],
  },
  {
    key: '/visual-replacement/image/models',
    label: '图片生成模型管理',
    section: '视觉生成',
    routePatterns: ['/visual-replacement/image/models'],
    apiRules: [api('/admin-api/v1/admin/visual-replacement/models/**')],
  },
  {
    key: '/visual-replacement/image/prompts',
    label: '图片生成提示词管理',
    section: '视觉生成',
    routePatterns: ['/visual-replacement/image/prompts'],
    apiRules: [
      api('/admin-api/v1/admin/visual-replacement/prompts/**'),
      api('/admin-api/v1/admin/visual-replacement/prompt-versions/**'),
    ],
  },
  {
    key: '/visual-replacement/video/templates',
    label: '视频生成模板管理',
    section: '视觉生成',
    routePatterns: ['/visual-replacement/video/templates'],
    apiRules: [
      api('/admin-api/v1/admin/visual-replacement/templates/**'),
      api('/admin-api/v1/admin/visual-replacement/template-categories/**'),
      api('/admin-api/v1/admin/visual-generation/upload', 'POST'),
      api('/admin-api/v1/admin/visual-generation/upload-batch', 'POST'),
    ],
  },
  {
    key: '/visual-replacement/video/models',
    label: '视频生成模型管理',
    section: '视觉生成',
    routePatterns: ['/visual-replacement/video/models'],
    apiRules: [api('/admin-api/v1/admin/visual-replacement/models/**')],
  },
  {
    key: '/visual-replacement/video/streaming',
    label: '视频流媒体处理',
    section: '视觉生成',
    routePatterns: ['/visual-replacement/video/streaming'],
    apiRules: [
      api('/admin-api/v1/admin/visual-replacement/templates/streaming-tasks', 'GET'),
      api('/admin-api/v1/admin/visual-replacement/templates/:id/streaming/retry', 'POST'),
      api('/admin-api/v1/admin/visual-replacement/templates/:id/streaming/status', 'PUT'),
    ],
  },
  {
    key: '/visual-replacement/video/prompts',
    label: '视频生成提示词管理',
    section: '视觉生成',
    routePatterns: ['/visual-replacement/video/prompts'],
    apiRules: [
      api('/admin-api/v1/admin/visual-replacement/prompts/**'),
      api('/admin-api/v1/admin/visual-replacement/prompt-versions/**'),
    ],
  },
  {
    key: '/visual-replacement/chat/models',
    label: '聊天管理模型管理',
    section: '视觉生成',
    routePatterns: ['/visual-replacement/chat/models'],
    apiRules: [api('/admin-api/v1/admin/visual-replacement/models/**')],
  },
  {
    key: '/visual-replacement/chat/prompts',
    label: '聊天管理提示词管理',
    section: '视觉生成',
    routePatterns: ['/visual-replacement/chat/prompts'],
    apiRules: [
      api('/admin-api/v1/admin/visual-replacement/prompts/**'),
      api('/admin-api/v1/admin/visual-replacement/prompt-versions/**'),
    ],
  },
  {
    key: '/image-generation/tasks',
    label: '任务管理',
    section: '图片生成管理',
    routePatterns: ['/image-generation/tasks'],
    apiRules: [
      api('/admin-api/v1/admin/image-generation/tasks/**'),
      api('/admin-api/v1/admin/image-generation/filter-options', 'GET'),
      api('/admin-api/v1/admin/image-generation/a-side/statistics', 'GET'),
      api('/admin-api/v1/admin/image-generation/c-side/statistics', 'GET'),
    ],
  },
  {
    key: '/image-generation/translation-records',
    label: '翻译记录',
    section: '图片生成管理',
    routePatterns: ['/image-generation/translation-records'],
    apiRules: [api('/admin-api/v1/admin/translation/records/**')],
  },
  {
    key: '/image-configs/types',
    label: '类型配置',
    section: '图片生成管理',
    routePatterns: ['/image-configs/types'],
    apiRules: [api('/admin-api/v1/admin/image-configs/types/**')],
  },
  {
    key: '/image-configs/models',
    label: '模型配置',
    section: '图片生成管理',
    routePatterns: ['/image-configs/models'],
    apiRules: [
      api('/admin-api/v1/admin/image-configs/models/**'),
      api('/admin-api/v1/admin/image-configs/types', 'GET'),
      api('/admin-api/v1/admin/image-generation/comfyui/checkpoints', 'GET'),
    ],
  },
  {
    key: '/image-configs/styles',
    label: '风格配置',
    section: '图片生成管理',
    routePatterns: ['/image-configs/styles'],
    apiRules: [
      api('/admin-api/v1/admin/image-configs/styles/**'),
      api('/admin-api/v1/admin/image-configs/models', 'GET'),
      api('/admin-api/v1/admin/image-generation/lora-library', 'GET'),
    ],
  },
  {
    key: '/image-configs/tags',
    label: '标签配置',
    section: '图片生成管理',
    routePatterns: ['/image-configs/tags'],
    apiRules: [
      api('/admin-api/v1/admin/image-configs/tags/**'),
      api('/admin-api/v1/admin/image-configs/tag-categories/**'),
    ],
  },
  {
    key: '/jimeng-whitelist',
    label: '既梦白名单',
    section: '图片生成管理',
    routePatterns: ['/jimeng-whitelist'],
    apiRules: [api('/admin-api/v1/admin/jimeng-whitelist/**')],
  },
  {
    key: '/image-generation/lora-library',
    label: 'Lora库管理',
    section: '图片生成管理',
    routePatterns: ['/image-generation/lora-library'],
    apiRules: [
      api('/admin-api/v1/admin/image-generation/lora-library/**'),
      api('/admin-api/v1/admin/image-generation/comfyui/loras', 'GET'),
    ],
  },
  {
    key: '/comfyui-workflow-logs',
    label: 'ComfyUI工作流日志',
    section: '图片生成管理',
    routePatterns: ['/comfyui-workflow-logs'],
    apiRules: [api('/admin-api/v1/admin/comfyui-workflow-logs/**')],
  },
  {
    key: '/jimeng-workflow-logs',
    label: '既梦工作流日志',
    section: '图片生成管理',
    routePatterns: ['/jimeng-workflow-logs'],
    apiRules: [api('/admin-api/v1/admin/jimeng-workflow-logs/**')],
  },
  {
    key: '/match/dashboard',
    label: '数据大盘',
    section: '匹配管理',
    routePatterns: ['/match/dashboard'],
    apiRules: [
      api('/admin-api/v1/admin/match/dashboard', 'GET'),
      api('/admin-api/v1/admin/match/trends', 'GET'),
      api('/admin-api/v1/admin/match/resend-push-stats', 'GET'),
      api('/admin-api/v1/admin/match/moments/stats', 'GET'),
    ],
  },
  {
    key: '/match/ai-female-users',
    label: 'AI女用户池',
    section: '匹配管理',
    routePatterns: [
      '/match/ai-female-users',
      '/match/ai-female-users/moments',
      '/match/ai-female-users/:id/moments',
    ],
    apiRules: [
      api('/admin-api/v1/admin/match/ai-female-users/**'),
      api('/admin-api/v1/admin/match/ai-female-users/:id/private-media', 'GET'),
      api('/admin-api/v1/admin/match/ai-female-users/:id/private-media/:media_id', 'GET'),
      api('/admin-api/v1/admin/match/ai-female-users/:id/private-media/upload', 'POST'),
      api('/admin-api/v1/admin/match/ai-female-users/:id/private-media/finalize', 'POST'),
      api('/admin-api/v1/admin/match/ai-female-users/:id/private-media/reorder', 'PUT'),
      api('/admin-api/v1/admin/match/ai-female-users/:id/private-media/:media_id', 'PUT'),
      api('/admin-api/v1/admin/match/ai-female-users/:id/private-media/:media_id', 'DELETE'),
      api('/admin-api/v1/admin/match/ai-female-users/:id/private-media/:media_id/restore', 'POST'),
      api(
        '/admin-api/v1/admin/match/ai-female-users/:id/private-media/:media_id/restore-source',
        'POST'
      ),
      api(
        '/admin-api/v1/admin/match/ai-female-users/:id/private-media/:media_id/cover/upload',
        'POST'
      ),
      api(
        '/admin-api/v1/admin/match/ai-female-users/:id/private-media/:media_id/cover/finalize',
        'POST'
      ),
      api('/admin-api/v1/admin/match/moments/**'),
      api('/admin-api/v1/admin/match/dashboard', 'GET'),
      api('/admin-api/v1/admin/match/segment-levels', 'GET'),
      api('/admin-api/v1/admin/character-tags', 'GET'),
      api('/admin-api/v1/admin/character-looking-for-tags', 'GET'),
      api('/admin-api/v1/admin/character-type-prefer-tags', 'GET'),
      api('/admin-api/v1/admin/files/upload', 'POST'),
    ],
  },
  {
    key: '/match/ai-female-users/anchor-profile-sync',
    label: '女角色跨包资料同步',
    section: '匹配管理',
    routePatterns: ['/match/ai-female-users'],
    apiRules: [
      api('/admin-api/v1/admin/match/ai-female-users/anchor-profile-sync/preview', 'POST'),
      api('/admin-api/v1/admin/match/ai-female-users/anchor-profile-sync/diff/:id', 'GET'),
      api('/admin-api/v1/admin/match/ai-female-users/anchor-profile-sync/jobs', 'POST'),
      api('/admin-api/v1/admin/match/ai-female-users/anchor-profile-sync/jobs/:job_id', 'GET'),
    ],
  },
  {
    key: '/match/deleted-ai-female-users',
    label: '删除 AI 女用户',
    section: '匹配管理',
    routePatterns: ['/match/deleted-ai-female-users'],
    apiRules: [api('/admin-api/v1/admin/match/deleted-ai-female-users', 'GET')],
  },
  {
    key: '/match/user',
    label: '用户查询',
    section: '匹配管理',
    routePatterns: ['/match/user'],
    apiRules: [
      api('/admin-api/v1/admin/match/user-overview/**', 'GET'),
      api('/admin-api/v1/admin/match/user-trace/**', 'GET'),
      api('/admin-api/v1/admin/match/ai-logs', 'GET'),
      api('/admin-api/v1/admin/match/segment-levels', 'GET'),
      api('/admin-api/v1/admin/match/swipe-records', 'GET'),
    ],
  },
  {
    key: '/match/moments',
    label: '动态列表',
    section: '匹配管理',
    routePatterns: ['/match/moments'],
    apiRules: [
      api('/admin-api/v1/admin/match/moments', 'GET'),
      api('/admin-api/v1/admin/match/moments/batch/visibility', 'POST'),
      api('/admin-api/v1/admin/match/moments/batch/delete', 'POST'),
      api('/admin-api/v1/admin/match/segment-levels', 'GET'),
    ],
  },
  {
    key: '/match/results',
    label: '匹配记录',
    section: '匹配管理',
    routePatterns: ['/match/results'],
    apiRules: [
      api('/admin-api/v1/admin/match/results/**'),
      api('/admin-api/v1/admin/match/resend-push-logs', 'GET'),
    ],
  },
  {
    key: '/match/chatted-characters',
    label: '已聊记录',
    section: '匹配管理',
    routePatterns: ['/match/chatted-characters'],
    apiRules: [api('/admin-api/v1/admin/match/chatted-characters', 'GET')],
  },
  {
    key: '/match/swipe-records',
    label: '划卡记录',
    section: '匹配管理',
    routePatterns: ['/match/swipe-records'],
    apiRules: [
      api('/admin-api/v1/admin/match/swipe-records', 'GET'),
      api('/admin-api/v1/admin/match/segment-levels', 'GET'),
      api('/admin-api/v1/admin/match/user-overview/**', 'GET'),
      api('/admin-api/v1/admin/characters/:id', 'GET'),
    ],
  },
  {
    key: '/match/quota',
    label: '配额管理',
    section: '匹配管理',
    routePatterns: ['/match/quota'],
    apiRules: [
      api('/admin-api/v1/admin/match/quota'),
      api('/admin-api/v1/admin/match/quota/reset-status', 'GET'),
      api('/admin-api/v1/admin/match/quota-exceeded-users', 'GET'),
    ],
  },
  {
    key: '/match/config',
    label: '匹配配置（含前 N 配置）',
    aliases: ['前N', '前 N', '前80', '前 80', '划卡一期', '优质名单', '分段推荐'],
    section: '匹配管理',
    routePatterns: ['/match/config'],
    apiRules: [
      api('/admin-api/v1/admin/match/config/**'),
      api('/admin-api/v1/admin/match/mode-configs'),
      api('/admin-api/v1/admin/match/mode-configs/readiness', 'GET'),
      api('/admin-api/v1/admin/match/phase1-profile', 'GET'),
      api('/admin-api/v1/admin/match/phase1-profile/validate', 'POST'),
      api('/admin-api/v1/admin/match/phase1-profile/publish', 'POST'),
      api('/admin-api/v1/admin/match/ai-female-users', 'GET'),
      api('/admin-api/v1/admin/match/recalculate-segments', 'POST'),
      api('/admin-api/v1/admin/match/rebuild-redis-pool', 'POST'),
      api('/admin-api/v1/admin/match/sync-segments', 'POST'),
    ],
  },
  {
    key: '/match/stats',
    label: 'AI日志统计',
    section: '匹配管理',
    routePatterns: ['/match/stats'],
    apiRules: [
      api('/admin-api/v1/admin/match/stats', 'GET'),
      api('/admin-api/v1/admin/match/ai-logs/**', 'GET'),
      api('/admin-api/v1/admin/match/quota', 'GET'),
    ],
  },
  {
    key: '/match/diagnostics',
    label: '匹配诊断',
    section: '匹配管理',
    routePatterns: ['/match/diagnostics'],
    apiRules: [
      api('/admin-api/v1/admin/match/diagnostics/**', 'GET'),
      api('/admin-api/v1/admin/match/alerts/**', 'GET'),
    ],
  },
  {
    key: '/match/city-neighbors',
    label: '周边城市缓存',
    section: '匹配管理',
    routePatterns: ['/match/city-neighbors'],
    apiRules: [api('/admin-api/v1/admin/match/city-neighbors/**')],
  },
  {
    key: '/match/admin-ops',
    label: '管理员操作',
    section: '匹配管理',
    routePatterns: ['/match/admin-ops'],
    apiRules: [
      api('/admin-api/v1/admin/match/clear-related', 'POST'),
      api('/admin-api/v1/admin/match/clear-related/recover', 'POST'),
      api('/admin-api/v1/admin/match/barrier-status', 'GET'),
      api('/admin-api/v1/admin/match/verify-admin-ops-password', 'POST'),
      api('/admin-api/v1/admin/match/ai-female-users/clear-pool', 'POST'),
    ],
  },
  {
    key: '/anchors/guilds',
    label: '工会配置',
    section: '主播管理',
    routePatterns: ['/anchors/guilds'],
    apiRules: [api('/admin-api/v1/admin/anchor-guilds/**')],
  },
  {
    key: '/anchors/guild-invites',
    label: '我的公会邀请码',
    section: '主播管理',
    routePatterns: ['/anchors/guild-invites'],
    apiRules: [api('/admin-api/v1/admin/anchor-guilds', 'GET')],
  },
  {
    key: '/anchors/levels',
    label: '主播等级',
    section: '主播管理',
    routePatterns: ['/anchors/levels'],
    apiRules: [api('/admin-api/v1/admin/anchor-levels/**')],
  },
  {
    key: '/anchors/level-rating-logs',
    label: '等级评级日志',
    section: '主播管理',
    routePatterns: ['/anchors/level-rating-logs'],
    apiRules: [
      api('/admin-api/v1/admin/anchor-level-rating-runs', 'GET'),
      api('/admin-api/v1/admin/anchor-level-rating-runs/**', 'GET'),
    ],
  },
  {
    key: '/anchors/level-rating-rerun',
    label: '等级评级重跑',
    section: '主播管理',
    routePatterns: [],
    apiRules: [api('/admin-api/v1/admin/anchor-level-rating-runs/:id/rerun', 'POST')],
  },
  {
    key: '/anchors/level-rating-run-now',
    label: '等级立即评级',
    section: '主播管理',
    routePatterns: [],
    apiRules: [api('/admin-api/v1/admin/anchor-level-rating-runs/run-now', 'POST')],
  },
  {
    key: '/anchors/pool',
    label: '主播池',
    section: '主播管理',
    routePatterns: ['/anchors/pool'],
    apiRules: [
      api('/admin-api/v1/admin/anchors/pool', 'GET'),
      api('/admin-api/v1/admin/anchors/pool/sync-ai-females', 'POST'),
      api('/admin-api/v1/admin/anchors/pool/sync-ai-female-galleries', 'POST'),
      api('/admin-api/v1/admin/anchors/pool/ai-female-private-media-sync-status', 'GET'),
      api('/admin-api/v1/admin/anchors/pool/sync-ai-female-private-media', 'POST'),
      api('/admin-api/v1/admin/anchors/pool/sync-ai-female-all', 'POST'),
      api('/admin-api/v1/admin/anchors', 'POST'),
      api('/admin-api/v1/admin/anchors/:id/profile', 'GET'),
      api('/admin-api/v1/admin/anchors/:id/business-metrics', 'GET'),
      api('/admin-api/v1/admin/anchors/:id/earnings', 'GET'),
      api('/admin-api/v1/admin/anchors/:id/activity-logs', 'GET'),
      api('/admin-api/v1/admin/anchors/:id/status', 'PATCH'),
      api('/admin-api/v1/admin/anchor-batches/status', 'POST'),
      api('/admin-api/v1/admin/anchors/:id/reset-password', 'POST'),
      api('/admin-api/v1/admin/anchors/:id/settings', 'PATCH'),
      api('/admin-api/v1/admin/anchors/:id/review', 'PATCH'),
      api('/admin-api/v1/admin/anchor-guilds/**', 'GET'),
      api('/admin-api/v1/admin/anchor-levels/**', 'GET'),
      api('/admin-api/v1/admin/apps/**', 'GET'),
      api('/admin-api/v1/admin/files/upload', 'POST'),
    ],
  },
  {
    key: '/anchors/call-records',
    label: '主播通话记录',
    section: '主播管理',
    routePatterns: ['/anchors/call-records'],
    apiRules: [
      api('/admin-api/v1/admin/anchor-call-records', 'GET'),
      api('/admin-api/v1/admin/anchor-guilds/**', 'GET'),
    ],
  },
  {
    key: '/anchors/frame-moderations',
    label: '视频抽帧审核',
    section: '主播管理',
    routePatterns: ['/anchors/frame-moderations'],
    apiRules: [
      api('/admin-api/v1/admin/video-call-frame-moderations', 'GET'),
      api('/admin-api/v1/admin/video-call-frame-moderations/:id', 'GET'),
    ],
  },
  {
    key: '/anchors/withdraw-requests',
    label: '主播提现记录',
    section: '主播管理',
    routePatterns: ['/anchors/withdraw-requests'],
    apiRules: [
      api('/admin-api/v1/admin/anchor-withdraw-requests', 'GET'),
      api('/admin-api/v1/admin/anchor-withdraw-requests/export', 'GET'),
      api('/admin-api/v1/admin/anchor-withdraw-requests/:id/review', 'POST'),
      api('/admin-api/v1/admin/anchor-withdraw-requests/:id/transfer', 'POST'),
    ],
  },
  {
    key: '/anchors/private-media',
    label: '私密媒体审核',
    aliases: ['私密视频审核'],
    section: '主播管理',
    routePatterns: ['/anchors/private-media'],
    apiRules: [
      api('/admin-api/v1/admin/anchor-private-media', 'GET'),
      api('/admin-api/v1/admin/anchor-private-media/:id/approve', 'POST'),
      api('/admin-api/v1/admin/anchor-private-media/:id/reject', 'POST'),
      api('/admin-api/v1/admin/anchor-private-media/batch-approve', 'POST'),
      api('/admin-api/v1/admin/anchor-private-media/batch-reject', 'POST'),
    ],
  },
  {
    key: '/anchors/revenue-dashboard',
    label: '工会收益看板',
    section: '主播管理',
    routePatterns: ['/anchors/revenue-dashboard'],
    apiRules: [
      api('/admin-api/v1/admin/anchor-revenue-dashboard', 'GET'),
      api('/admin-api/v1/admin/anchor-revenue-dashboard/anchors', 'GET'),
      api('/admin-api/v1/admin/anchor-revenue-dashboard/withdraw-requests', 'GET'),
      api('/admin-api/v1/admin/anchor-guilds/**', 'GET'),
    ],
  },
  {
    key: '/anchors/online-stats',
    label: '主播在线统计',
    section: '主播管理',
    routePatterns: ['/anchors/online-stats'],
    apiRules: [
      api('/admin-api/v1/admin/anchor-online-stats', 'GET'),
      api('/admin-api/v1/admin/anchor-online-stats/items', 'GET'),
      api('/admin-api/v1/admin/anchor-guilds/**', 'GET'),
    ],
  },
  {
    key: '/videocall/dashboard',
    label: '通话监控',
    section: '视频通话',
    routePatterns: ['/videocall/dashboard'],
    apiRules: [
      api('/admin-api/v1/admin/videocall/dashboard', 'GET'),
      api('/admin-api/v1/admin/videocall/orders/:order_id/trace', 'GET'),
    ],
  },
  {
    key: '/settings/apps',
    label: 'APP管理',
    section: '系统设置',
    routePatterns: ['/settings/apps'],
    apiRules: [api('/admin-api/v1/admin/apps/**')],
  },
  {
    key: '/settings/app-store-links',
    label: '应用商店链接',
    section: '系统设置',
    routePatterns: ['/settings/app-store-links'],
    apiRules: [api('/admin-api/v1/admin/app-store-links/**')],
  },
  {
    key: '/settings/versions',
    label: 'APP版本管理',
    section: '系统设置',
    routePatterns: ['/settings/versions'],
    apiRules: [api('/admin-api/v1/admin/app-moderation-configs/**')],
  },
  {
    key: '/settings/base-limits',
    label: '基础限制配置',
    section: '系统设置',
    routePatterns: ['/settings/base-limits'],
    apiRules: [api('/admin-api/v1/admin/base-limits/**')],
  },
  {
    key: '/settings/service-accounts',
    label: '服务账号管理',
    section: '系统设置',
    routePatterns: ['/settings/service-accounts'],
    apiRules: [api('/admin-api/v1/admin/package-service-accounts/**')],
  },
  {
    key: '/settings/translation-configs',
    label: '翻译配置',
    section: '系统设置',
    routePatterns: ['/settings/translation-configs'],
    apiRules: [api('/admin-api/v1/admin/translation/configs/**')],
  },
  {
    key: '/settings/firebase',
    label: 'Firebase配置',
    section: '系统设置',
    routePatterns: ['/settings/firebase'],
    apiRules: [api('/admin-api/v1/admin/firebase-configs/**')],
  },
  {
    key: '/settings/oauth-configs',
    label: '三方登录配置',
    section: '系统设置',
    routePatterns: ['/settings/oauth-configs'],
    apiRules: [api('/admin-api/v1/admin/oauth-configs/**')],
  },
  {
    key: '/settings/onelink-configs',
    label: 'OneLink配置',
    section: '系统设置',
    routePatterns: ['/settings/onelink-configs'],
    apiRules: [api('/admin-api/v1/admin/onelink-configs/**')],
  },
  {
    key: '/rooms/chat-relationship-stages',
    label: '聊天关系阶段配置',
    section: '聊天管理',
    routePatterns: ['/rooms/chat-relationship-stages'],
    apiRules: [
      api('/admin-api/v1/admin/chat-strategy/relationship-stages', 'GET'),
      api('/admin-api/v1/admin/chat-strategy/relationship-stages', 'POST'),
      api('/admin-api/v1/admin/chat-strategy/relationship-stages/:id', 'PUT'),
      api('/admin-api/v1/admin/chat-strategy/relationship-stages/:id', 'DELETE'),
    ],
  },
  {
    key: '/settings/chat-ai-configs',
    label: 'Chat AI配置',
    section: '系统设置',
    routePatterns: [
      '/settings/chat-ai-configs',
      '/settings/chat-ai-configs/new',
      '/settings/chat-ai-configs/:profileId',
    ],
    apiRules: [
      api('/admin-api/v1/admin/chat-ai-configs/**'),
      api('/admin-api/v1/admin/chat-rag/**'),
      api('/admin-api/v1/admin/chat-sensitive-words/**'),
      api('/admin-api/v1/admin/translation/translate-to-chinese', 'POST'),
    ],
  },
  {
    key: '/settings/ai-token-usage',
    label: '大模型管理',
    section: '系统设置',
    routePatterns: ['/settings/ai-token-usage'],
    apiRules: [api('/admin-api/v1/admin/chat-ai-configs/token-usage/**')],
  },
  {
    key: '/chat-ai/prompt-workspace',
    label: 'Prompt 工作台',
    section: 'Chat AI',
    routePatterns: ['/chat-ai/prompt-workspace'],
    apiRules: [
      api('/admin-api/v1/admin/chat-ai/prompt-assets', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-assets', 'POST'),
      api('/admin-api/v1/admin/chat-ai/prompt-assets/cross-app-copy/preview', 'POST'),
      api('/admin-api/v1/admin/chat-ai/prompt-assets/cross-app-copy/execute', 'POST'),
      api('/admin-api/v1/admin/chat-ai/prompt-assets/:id', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-assets/:id', 'PUT'),
      api('/admin-api/v1/admin/chat-ai/prompt-assets/:id/duplicate', 'POST'),
      api('/admin-api/v1/admin/chat-ai/prompt-assets/:id/disable', 'POST'),
      api('/admin-api/v1/admin/chat-ai/prompt-assets/:id/references', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-assets/:id/versions', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-assets/:id/versions', 'POST'),
      api('/admin-api/v1/admin/chat-ai/prompt-versions/:id', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-versions/:id', 'PUT'),
      api('/admin-api/v1/admin/chat-ai/prompt-versions/:id/validate', 'POST'),
      api('/admin-api/v1/admin/chat-ai/prompt-versions/:id/publish', 'POST'),
      api('/admin-api/v1/admin/chat-ai/prompt-versions/:id/deprecate', 'POST'),
      api('/admin-api/v1/admin/chat-ai/prompt-asset-registry', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-versions/render-preview', 'POST'),
      api('/admin-api/v1/admin/chat-ai-configs/app/:appId/profiles', 'GET'),
    ],
  },
  {
    key: '/chat-ai/prompt-sample-sets',
    label: 'Prompt样本集',
    section: 'Chat AI',
    routePatterns: ['/chat-ai/prompt-sample-sets'],
    apiRules: [
      api('/admin-api/v1/admin/chat-ai/prompt-sample-sets/workspace', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-sample-sets/room-messages', 'GET'),
      api(
        '/admin-api/v1/admin/chat-ai/prompt-sample-sets/prompt-experiment-run-sedimentations',
        'GET'
      ),
      api('/admin-api/v1/admin/chat-ai/prompt-sample-sets', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-sample-sets', 'POST'),
      api('/admin-api/v1/admin/chat-ai/prompt-sample-sets/from-prompt-experiment-run', 'POST'),
      api('/admin-api/v1/admin/chat-ai/prompt-sample-sets/:id', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-sample-sets/:id', 'PUT'),
      api('/admin-api/v1/admin/chat-ai/prompt-sample-sets/:id', 'DELETE'),
      api('/admin-api/v1/admin/chat-ai/prompt-sample-sets/:id/duplicate', 'POST'),
      api('/admin-api/v1/admin/chat-ai/prompt-sample-sets/:id/import-room', 'POST'),
      api('/admin-api/v1/admin/chat-ai/prompt-sample-sets/:id/import-harness-run', 'POST'),
      api(
        '/admin-api/v1/admin/chat-ai/prompt-sample-sets/:id/import-prompt-experiment-run',
        'POST'
      ),
    ],
  },
  {
    key: '/chat-ai/prompt-experiments',
    label: 'Prompt实验',
    section: 'Chat AI',
    routePatterns: ['/chat-ai/prompt-experiments'],
    apiRules: [
      api('/admin-api/v1/admin/chat-ai/prompt-experiments/workspace', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-experiments', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-experiments', 'POST'),
      api('/admin-api/v1/admin/chat-ai/prompt-experiments/:id', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-experiments/:id', 'PUT'),
      api('/admin-api/v1/admin/chat-ai/prompt-experiments/:id', 'DELETE'),
      api('/admin-api/v1/admin/chat-ai/prompt-experiments/:id/runs', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-experiments/:id/runs', 'POST'),
      api('/admin-api/v1/admin/chat-ai/prompt-experiment-runs/:run_id', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-experiment-runs/:run_id/evaluations', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-experiment-runs/:run_id/auto-evaluations', 'POST'),
      api('/admin-api/v1/admin/chat-ai/prompt-experiment-runs/:run_id/manual-annotations', 'POST'),
      api('/admin-api/v1/admin/chat-ai-configs/app/:appId/profiles', 'GET'),
      api('/admin-api/v1/admin/chat-ai-configs/prompt-versions/profile/:configId', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-sample-sets', 'GET'),
      api(
        '/admin-api/v1/admin/chat-ai/prompt-sample-sets/prompt-experiment-run-sedimentations',
        'GET'
      ),
      api('/admin-api/v1/admin/chat-ai/prompt-sample-sets/from-prompt-experiment-run', 'POST'),
      api(
        '/admin-api/v1/admin/chat-ai/prompt-sample-sets/:id/import-prompt-experiment-run',
        'POST'
      ),
    ],
  },
  {
    key: '/chat-ai/prompt-review-center',
    label: 'Prompt评审中心',
    section: 'Chat AI',
    routePatterns: ['/chat-ai/prompt-review-center', '/chat-ai/prompt-annotations'],
    apiRules: [
      api('/admin-api/v1/admin/chat-ai/prompt-annotations/workspace', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-experiments', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-experiments/:id/runs', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-experiment-runs/:run_id', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-experiment-runs/:run_id/evaluations', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-experiment-runs/:run_id/auto-evaluations', 'POST'),
      api('/admin-api/v1/admin/chat-ai/prompt-experiment-runs/:run_id/manual-annotations', 'POST'),
      api('/admin-api/v1/admin/chat-ai/prompt-sample-sets/from-prompt-experiment-run', 'POST'),
    ],
  },
  {
    key: '/chat-ai/prompt-evaluation-center',
    label: '测评中心',
    section: 'Chat AI',
    routePatterns: ['/chat-ai/prompt-evaluation-center'],
    apiRules: [
      api('/admin-api/v1/admin/chat-ai/prompt-evaluation-templates', 'GET'),
      api('/admin-api/v1/admin/chat-ai/prompt-evaluation-templates', 'POST'),
      api('/admin-api/v1/admin/chat-ai/prompt-evaluation-templates/render-preview', 'POST'),
      api('/admin-api/v1/admin/chat-ai/prompt-evaluation-templates/:id', 'PUT'),
      api('/admin-api/v1/admin/chat-ai/prompt-evaluation-templates/:id', 'DELETE'),
      api('/admin-api/v1/admin/chat-ai/prompt-evaluation-templates/:id/activate', 'POST'),
    ],
  },
  {
    key: '/chat-ai/agent-flow-node-catalogs',
    label: 'Agent Flow 节点目录',
    section: 'Chat AI',
    routePatterns: ['/chat-ai/agent-flow-node-catalogs'],
    apiRules: [
      api('/admin-api/v1/admin/chat-ai/agent-flow-node-capabilities', 'GET'),
      api('/admin-api/v1/admin/chat-ai/agent-flow-node-catalogs', 'GET'),
      api('/admin-api/v1/admin/chat-ai/agent-flow-node-catalogs', 'POST'),
      api('/admin-api/v1/admin/chat-ai/agent-flow-node-catalogs/:id', 'GET'),
      api('/admin-api/v1/admin/chat-ai/agent-flow-node-catalogs/:id', 'PUT'),
      api('/admin-api/v1/admin/chat-ai/agent-flow-node-catalogs/:id', 'DELETE'),
      api('/admin-api/v1/admin/chat-ai/agent-flow-node-catalogs/:id/duplicate', 'POST'),
      api('/admin-api/v1/admin/chat-ai/agent-flow-node-catalogs/:id/disable', 'POST'),
      api('/admin-api/v1/admin/chat-ai/agent-flow-node-catalogs/:id/references', 'GET'),
    ],
  },
  {
    key: '/chat-ai/agent-flows',
    label: 'Agent Flow',
    section: 'Chat AI',
    routePatterns: ['/chat-ai/agent-flows'],
    apiRules: [
      api('/admin-api/v1/admin/chat-ai/agent-flows/registry-metadata', 'GET'),
      api('/admin-api/v1/admin/chat-ai/agent-flow-node-catalogs', 'GET'),
      api('/admin-api/v1/admin/chat-ai/agent-flows', 'GET'),
      api('/admin-api/v1/admin/chat-ai/agent-flows', 'POST'),
      api('/admin-api/v1/admin/chat-ai/agent-flows/:id', 'GET'),
      api('/admin-api/v1/admin/chat-ai/agent-flow-releases/:release_id', 'PUT'),
      api('/admin-api/v1/admin/chat-ai/agent-flow-releases/:release_id/duplicate', 'POST'),
      api('/admin-api/v1/admin/chat-ai/agent-flow-releases/:release_id/validate', 'POST'),
      api('/admin-api/v1/admin/chat-ai/agent-flow-releases/:release_id/publish', 'POST'),
      api('/admin-api/v1/admin/chat-ai/agent-flow-releases/:release_id/deprecate', 'POST'),
      api('/admin-api/v1/admin/chat-ai/agent-flow-bindings', 'PUT'),
      api('/admin-api/v1/admin/chat-ai/agent-flow-bindings/active', 'GET'),
      api('/admin-api/v1/admin/chat-ai/agent-flow-bindings/disable', 'POST'),
      api('/admin-api/v1/admin/chat-ai-configs/app/:app_id/profiles', 'GET'),
      api('/admin-api/v1/admin/chat-ai-configs/prompt-versions/profile/:config_id', 'GET'),
    ],
  },
  {
    key: '/agent-harness',
    label: 'Agent Harness',
    section: 'Agent Harness',
    routePatterns: ['/agent-harness', '/agent-harness/runs', '/agent-harness/config'],
    apiRules: [
      api('/admin-api/v1/admin/agent-harness/config', 'GET'),
      api('/admin-api/v1/admin/agent-harness/config', 'PUT'),
      api('/admin-api/v1/admin/agent-harness/runs', 'GET'),
      api('/admin-api/v1/admin/agent-harness/runs/:id', 'GET'),
      api('/admin-api/v1/admin/agent-harness/runs/:id/debug-logs', 'GET'),
      // Chat-list intent inspection exposes the same class of authorized Raw
      // prompt/model evidence as the Agent Harness run detail.
      api('/admin-api/v1/admin/rooms/:id/messages/:messageId/intent-inspection', 'POST'),
    ],
  },
  {
    key: '/settings/alert-bots',
    label: '告警机器人',
    section: '系统设置',
    routePatterns: ['/settings/alert-bots'],
    apiRules: [api('/admin-api/v1/admin/alert-bots/**')],
  },
  {
    key: '/settings/app-costs',
    label: '应用费用配置',
    section: '系统设置',
    routePatterns: ['/settings/app-costs'],
    apiRules: [api('/admin-api/v1/admin/app-costs/**')],
  },
  {
    key: '/settings/system-configs',
    label: '系统配置',
    section: '系统设置',
    routePatterns: ['/settings/system-configs'],
    apiRules: [api('/admin-api/v1/admin/system-configs/**')],
  },
  {
    key: '/settings/interaction-signal-copy',
    label: '互动信号文案',
    section: '系统设置',
    routePatterns: ['/settings/interaction-signal-copy'],
    apiRules: [api('/admin-api/v1/admin/system-configs/**')],
  },
  {
    key: '/settings/event-system',
    label: '事件系统',
    section: '系统设置',
    routePatterns: ['/settings/event-system'],
    apiRules: [
      api('/admin-api/v1/admin/event-configs/**'),
      api('/admin-api/v1/admin/event-runtime/**'),
      api('/admin-api/v1/admin/event-runtime/opening-queue-items', 'GET'),
      api('/admin-api/v1/admin/event-runtime/opening-daily-counters', 'GET'),
      api('/admin-api/v1/admin/event-tasks/**'),
      api('/admin-api/v1/admin/event-task-executions/**'),
    ],
  },
  {
    key: '/settings/redis-tools',
    label: 'Redis 管理',
    section: '系统设置',
    routePatterns: ['/settings/redis-tools'],
    apiRules: [api('/admin-api/v1/admin/redis-tools/**')],
  },
  {
    key: '/settings/first-login-rewards',
    label: '首次登录送金币',
    section: '系统设置',
    routePatterns: ['/settings/first-login-rewards'],
    apiRules: [api('/admin-api/v1/admin/first-login-reward-configs/**')],
  },
  {
    key: '/settings/app-version-review-rewards',
    label: '审核版本送金币',
    section: '系统设置',
    routePatterns: ['/settings/app-version-review-rewards'],
    apiRules: [api('/admin-api/v1/admin/app-version-review-reward-configs/**')],
  },
  {
    key: '/settings/operation-logs',
    label: '操作日志',
    section: '系统设置',
    routePatterns: ['/settings/operation-logs'],
    apiRules: [api('/admin-api/v1/admin/operation-logs/**')],
  },
]

export const ADMIN_PERMISSION_PAGE_MAP = new Map(
  ADMIN_PERMISSION_CATALOG.map((item) => [item.key, item])
)

export const ADMIN_PERMISSION_ALL_PAGE_KEYS = ADMIN_PERMISSION_CATALOG.map((item) => item.key)
